/*
  Cart maths shared by the cart's kit-upgrade box (snippets/af-kit-upgrade) and the kit builder
  (snippets/af-kit-builder). Pure functions over /cart.js items, tested in
  scripts/theme_tests/af-cart-plan.test.js (node --test).

  Every plan is the `updates` object of ONE /cart/update.js call: a line already in the cart is
  addressed by its line key, a variant not yet in the cart by its variant id, never both for the
  same variant (one call can zero a line, add a variant and set attributes, verified 25 Sep 2026).
  One call means a failed request never leaves the customer between "old kit removed" and "new
  kit added".

  ids = { v1: single-kit variant, v2: "2 x Kits" variant, dk: standalone Double Kit product variant }
*/
(function (root) {
  function kitLines(items, ids) {
    var o = { v1: [], v2: [], dk: [] };
    (items || []).forEach(function (it) {
      if (it.variant_id == ids.v1) o.v1.push(it);
      else if (it.variant_id == ids.v2) o.v2.push(it);
      else if (it.variant_id == ids.dk) o.dk.push(it);
    });
    return o;
  }

  function qty(lines) {
    return lines.reduce(function (a, l) { return a + l.quantity; }, 0);
  }

  /* A variant's total quantity: carried by its first existing line (any others go to 0), or
     added by variant id when it is not in the cart yet. */
  function setTotal(updates, lines, variantId, n) {
    if (lines.length) lines.forEach(function (l, i) { updates[l.key] = i === 0 ? n : 0; });
    else if (n > 0) updates[variantId] = n;
  }

  /* As many "2 x Kits" as possible, one single for an odd count. The upgrade box offers
     "switch and save" on any pair of singles, so no recommendation may contain a pair. */
  function kitForm(kits) {
    if (kits < 2) return { doubles: 0, singles: kits };
    return { doubles: Math.floor(kits / 2), singles: kits % 2 };
  }

  /* What the cart's upgrade box offers, or null when the kits are already in their cheapest form.
     'one'  : exactly one single kit, step up to the double variant.
     'pairs': two or more singles, every pair becomes a double (same kits, cheaper). */
  function upgradePlan(items, ids) {
    var k = kitLines(items, ids), s = qty(k.v1), d = qty(k.v2), dk = qty(k.dk), updates = {};
    if (s === 1 && d === 0 && dk === 0) {
      setTotal(updates, k.v1, ids.v1, 0);
      setTotal(updates, k.v2, ids.v2, 1);
      return { mode: 'one', pairs: 0, updates: updates };
    }
    if (s >= 2) {
      var pairs = Math.floor(s / 2);
      setTotal(updates, k.v1, ids.v1, s % 2);
      setTotal(updates, k.v2, ids.v2, d + pairs);
      return { mode: 'pairs', pairs: pairs, updates: updates };
    }
    return null;
  }

  /* The builder's answer is how many kits the job needs, so kit lines already in the cart are
     REPLACED by the recommendation, never added to (a single in the cart + "take the Double
     Kit" used to end at three kits). The standalone Double Kit product is folded into the
     variant. Add-ons [{id, quantity}] go on top of whatever is there. */
  function builderPlan(items, kits, ids, addons) {
    var k = kitLines(items, ids), f = kitForm(kits), updates = {};
    setTotal(updates, k.v1, ids.v1, f.singles);
    setTotal(updates, k.v2, ids.v2, f.doubles);
    k.dk.forEach(function (l) { updates[l.key] = 0; });
    (addons || []).forEach(function (a) {
      var existing = (items || []).filter(function (it) { return it.variant_id == a.id; })[0];
      if (existing) updates[existing.key] = existing.quantity + a.quantity;
      else updates[a.id] = (updates[a.id] || 0) + a.quantity;
    });
    return updates;
  }

  /* Metres of edge to mask: each item counted as the perimeter of a square of its surface
     (a 1,2 m² window is ~4,4 m). An estimate on purpose, the builder shows it as "about". */
  function edgeMetres(counts, m2) {
    return counts.reduce(function (a, c, i) { return a + c * 4 * Math.sqrt(m2[i]); }, 0);
  }

  /* How much Q1 tape the builder suggests for `metres` of edge, as the two variants we sell:
     { singles: n (1-roll variant), triples: m (3-roll variant) }, each roll `rollMetres` long.
     The pill shows the combined price; { singles: 0, triples: 0 } hides the tape option.
     Owner rule (25 Sep 2026): a roll is 50 m, so any metre past it needs another roll; never
     short, or someone runs out halfway round a window. That count is then bought the cheapest
     way: 3 rolls come as the €12,95 pack, but a remainder of 2 stays two singles (2 x €4,95 =
     €9,90 is less than the pack). */
  function tapeRolls(metres, rollMetres) {
    var rolls = metres > 0 ? Math.ceil(metres / rollMetres) : 0;
    return { singles: rolls % 3, triples: Math.floor(rolls / 3) };
  }

  /* The complete set (code KITSET, always available since 25 Sep 2026): €11,95 off when the cart
     holds at least €86,90 of {the single-kit variant, any tape variant, any gloves variant}. The
     Double Kit variant is deliberately NOT eligible, or the code would be a plain discount on any
     Double Kit (the email's COMPLETESET follows the same rule at €18,94 until 27 Sep). s = { kit: single-kit variant, tape: 1-roll variant, gloves: 1-pair variant,
     tapeAll: [every tape variant], glovesAll: [every gloves variant] } */
  function has(items, ids) {
    return (items || []).some(function (it) { return ids.some(function (id) { return it.variant_id == id; }); });
  }
  function setComplete(items, s) {
    return has(items, [s.kit]) && has(items, s.tapeAll) && has(items, s.glovesAll);
  }
  /* "Complete the set" adds only what is missing, by variant id (none of it is in the cart). A
     Double Kit counts as the kit (PREPSET2 carries the set on a Double Kit), so no single is added. */
  function setPlan(items, s) {
    var updates = {};
    if (!has(items, [s.kit].concat(s.kitDouble || []))) updates[s.kit] = 1;
    if (!has(items, s.tapeAll)) updates[s.tape] = 1;
    if (!has(items, s.glovesAll)) updates[s.gloves] = 1;
    return updates;
  }

  /* Browser glue for the upgrade box: re-read the live cart at click time and apply the plan in
     one call, tagging the cart so orders that took it can be counted ('swap' steps up from one
     kit, 'pairs' gives back money on doubled singles). Resolves to the plan, or null when the cart
     changed and nothing is left to upgrade. */
  function applyUpgrade(ids, setCfg) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var plan = upgradePlan(cart.items, ids);
        if (!plan) return null;
        var attrs = { 'Kit upgrade': plan.mode === 'one' ? 'swap' : 'pairs' };
        var body = { updates: plan.updates, attributes: attrs };
        var carried = setCfg ? carryCodes(cart.discount_codes, setCfg) : null;
        if (carried) body.discount = carried.join(',');
        return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body) })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/update.js ' + r.status);
            try { if (root.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('af_cart_upgrade', { mode: plan.mode, pairs: plan.pairs }); } catch (err) {}
            return plan;
          });
      });
  }

  /* Which set offer a cart gets (owner + Seb, 25 Sep 2026): a set cheaper than the kit must never
     be shown to a full-price buyer. 'code' when a whole-order discount CODE is applied (WELCOME10,
     the popup's 10%): they get the KITSET set (€74,95), which beats their code. Otherwise 'full':
     the PREPSET set, tape + gloves for €5 with the kit (€84,95; at €2 the ~20% who already buy
     gloves would get the tape free). Our set codes are product-specific
     ('entitled'), so taking PREPSET never turns a customer into a code user.
     apps = /cart.js cart_level_discount_applications (Liquid: cart.discount_applications). */
  function setTier(apps) {
    return (apps || []).some(function (a) { return a.type === 'discount_code' && a.target_selection === 'all'; }) ? 'code' : 'full';
  }

  /* Singles and doubles in a cart; the standalone Double Kit counts as a double. */
  function formOf(items, s) {
    var singles = 0, doubles = 0;
    (items || []).forEach(function (it) {
      if (it.variant_id == s.kit) singles += it.quantity;
      else if ((s.kitDouble || []).some(function (id) { return it.variant_id == id; })) doubles += it.quantity;
    });
    return { singles: singles, doubles: doubles };
  }

  /* Which set code fits this cart, or null. Code users only with a single kit: on a Double Kit
     their 10% already saves more than the set, and Shopify keeps the better code. Full-price carts
     get PREPSET when a single kit is (or will be) there, PREPSET2 when only a Double Kit is. */
  function setOffer(tier, form, s) {
    var t = s.tiers[tier];
    if (tier === 'code') return form.doubles === 0 ? { code: t.code, save: t.save } : null;
    return { code: form.singles > 0 || form.doubles === 0 ? t.code : t.double, save: t.save };
  }

  /* A swap to the Double Kit drops the single the full-price set code needs; carry the set over by
     appending its Double Kit code. Returns the new code list, or null when there is nothing to
     carry (no set applied, or a code user, whose own 10% applies again by itself). */
  function carryCodes(discountCodes, s) {
    var full = String(s.tiers.full.code).toUpperCase();
    var on = (discountCodes || []).some(function (d) { return d.applicable && String(d.code).toUpperCase() === full; });
    if (!on) return null;
    return (discountCodes || []).map(function (d) { return String(d.code); }).concat([s.tiers.full.double]);
  }

  /* Browser glue for "complete the set" (cart banner, kit page card): add what is missing and
     apply the code in one call. The tier is read from the live cart at click time, so the code
     applied is the one the offer on screen named. The `discount` field REPLACES the cart's code
     list, so the code is appended to whatever the customer already entered; with two codes that
     do not combine, Shopify applies the better one and keeps the other as a fallback (verified
     25 Sep 2026 with WELCOME10). cfg = the JSON from snippets/af-set-data. */
  function applySet(cfg, source) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var tier = setTier(cart.cart_level_discount_applications);
        var offer = setOffer(tier, formOf(cart.items, cfg), cfg);
        if (!offer) return null;
        var code = String(offer.code), codes = (cart.discount_codes || [])
          .map(function (d) { return String(d.code); })
          .filter(function (c) { return c.toUpperCase() !== code.toUpperCase(); });
        codes.push(code);
        return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: setPlan(cart.items, cfg), discount: codes.join(','), attributes: { 'Complete set': source + ' ' + tier } }) })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/update.js ' + r.status);
            try { if (root.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('af_complete_set', { source: source, tier: tier }); } catch (err) {}
            return r.json();
          });
      });
  }

  var P = { applySet: applySet, setTier: setTier, setOffer: setOffer, formOf: formOf, carryCodes: carryCodes, kitForm: kitForm, upgradePlan: upgradePlan, builderPlan: builderPlan, edgeMetres: edgeMetres,
            tapeRolls: tapeRolls, setComplete: setComplete, setPlan: setPlan, applyUpgrade: applyUpgrade };
  if (typeof module === 'object' && module.exports) module.exports = P; else root.AfCartPlan = P;
})(this);
